import { Autocomplete } from "@base-ui/react";
import { cx } from "class-variance-authority";
import { ArrowUpRightIcon, Globe, XIcon } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTypedSelector } from "@/store/store";
import { updateCanvasStoryboards } from "@/store/supabaseSlice";
import { useStoryboardHistoryStore } from "@/store/zustand/storyboardHistoryStore";
import { extractApplicationStoryboardUrl } from "@/utils/iframeUtils";
import { useActiveCanvas, useCanvasId, useProjectId } from "@/utils/utils";
import { useIframePoolStore } from "../IframePool/iframePoolStore";

const NewItemSymbol = Symbol("NewItemSymbol");

interface InnerUrlComboboxProps {
  urlOptions: (
    | symbol
    | { type: "storyboard"; value: string }
    | { type: "visited"; value: string }
  )[];
  disabled: boolean;
  currentUrl: string;
  devServerUrl: string;
  onSubmit: (url: string) => void;
  onRemoveVisitedUrl: (url: string) => void;
  open?: boolean;
  inputValue?: string;
}

export const InnerUrlCombobox = ({
  urlOptions,
  disabled,
  currentUrl,
  devServerUrl,
  onSubmit,
  onRemoveVisitedUrl,
  open,
  inputValue: initialInputValue,
}: InnerUrlComboboxProps) => {
  const [inputValue, setInputValue] = useState(initialInputValue ?? "");
  const inputRef = useRef<HTMLInputElement>(null);

  const formId = useId();

  const filteredUrlOptions = useMemo(() => {
    return urlOptions.filter(
      (option) =>
        (typeof option !== "symbol" &&
          option.value.includes(inputValue) &&
          option.value !== (inputValue.trim() || "/")) ||
        option === NewItemSymbol,
    );
  }, [urlOptions, inputValue]);

  useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      setInputValue(currentUrl);
    }
  }, [currentUrl]);

  return (
    <form
      id={formId}
      onSubmit={(e) => {
        e.preventDefault();
        let newUrl: string;
        try {
          newUrl = extractApplicationStoryboardUrl(
            new URL(inputValue, devServerUrl).href,
            devServerUrl,
          );

          if (newUrl === currentUrl) {
            return;
          }

          setInputValue(newUrl);
        } catch {
          // don't update the storyboard url if the url is invalid
          return;
        }
        onSubmit(newUrl);
      }}
      className="contents"
    >
      <Autocomplete.Root
        items={urlOptions}
        mode="both"
        value={inputValue}
        onValueChange={setInputValue}
        submitOnItemClick
        autoHighlight="always"
        filteredItems={filteredUrlOptions}
        itemToStringValue={(item) =>
          typeof item === "symbol" ? inputValue : item.value
        }
        limit={5}
        open={open}
      >
        <Autocomplete.Value>
          {(value) => (
            <Autocomplete.Trigger
              disabled={disabled}
              className={cn(
                "flex-1 bg-background-tertiary border-none outline-hidden text-ellipsis whitespace-nowrap truncate p-4 rounded-[12px] text-left",
                disabled && "cursor-not-allowed opacity-50",
                "hover:bg-background-primary hover:text-text-primary",
              )}
            >
              <Globe className="inline-block w-8 h-8 mr-4" />
              {value}
            </Autocomplete.Trigger>
          )}
        </Autocomplete.Value>
        <Autocomplete.Portal
          container={document.getElementById("canvas-panel")}
        >
          <Autocomplete.Positioner
            collisionBoundary={
              document.getElementById("canvas-panel") ?? undefined
            }
            sideOffset={(x) => -x.anchor.height - 3}
          >
            <Autocomplete.Popup
              className={cx(
                "w-[calc(var(--anchor-width)+6px)] max-h-(--available-height) max-w-(--available-width) p-0.5",
                "shadow-md rounded-md border border-primary bg-background-tertiary",
                "overflow-hidden",
              )}
            >
              <div className="flex items-center p-1.5 border border-primary-0 bg-background-primary rounded-[6px]">
                <Globe className="w-4 h-4 mr-2" />
                <Autocomplete.Input
                  type="text"
                  ref={inputRef}
                  form={formId}
                  disabled={disabled}
                  onFocus={(e) => e.target.select()}
                  className={cn("w-full text-2xs bg-transparent")}
                  placeholder="Enter URL"
                />
              </div>
              <Autocomplete.List className="p-1">
                {(item: (typeof urlOptions)[number]) => (
                  <Autocomplete.Item
                    key={typeof item === "symbol" ? 0 : item.value}
                    value={item}
                    className={cn(
                      "text-2xs pl-1.5 pr-2.5 py-1.5 rounded-sm",
                      "text-primary transition-colors duration-150",
                      "data-highlighted:bg-background-secondary data-highlighted:text-primary",
                      "cursor-pointer",
                      "flex items-center justify-between",
                      "group",
                    )}
                  >
                    <span className="shrink text-ellipsis whitespace-nowrap overflow-hidden">
                      <ArrowUpRightIcon className="w-3 h-3 inline-block mr-2 text-icons-muted group-data-highlighted:text-icons-primary" />
                      {typeof item === "symbol"
                        ? inputValue.trim() || "/"
                        : item.value}
                    </span>
                    {typeof item === "object" && item.type === "visited" && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="rounded-full w-5 h-5 shrink-0 hidden group-hover:flex -my-1 ml-1 -mr-1"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onRemoveVisitedUrl(item.value);
                        }}
                      >
                        <XIcon className="w-3 h-3" />
                      </Button>
                    )}
                  </Autocomplete.Item>
                )}
              </Autocomplete.List>
            </Autocomplete.Popup>
          </Autocomplete.Positioner>
        </Autocomplete.Portal>
      </Autocomplete.Root>
    </form>
  );
};
interface UrlComboboxProps {
  storyboardId: string;
  disabled?: boolean;
}

export const UrlCombobox = ({
  storyboardId,
  disabled = false,
}: UrlComboboxProps) => {
  const canvas = useActiveCanvas();
  const canvasId = useCanvasId();
  const projectId = useProjectId() ?? "";

  const storyboard = useMemo(
    () => canvas?.storyboards?.find((sb) => sb.id === storyboardId),
    [canvas, storyboardId],
  );

  const canvasIdToDevServerUrl = useTypedSelector(
    (state) => state.iframe.canvasIdToDevServerUrl,
  );
  const devServerUrl = canvasIdToDevServerUrl[canvasId];

  const currentUrl = storyboard?.route ?? "";

  const visitedUrls = useStoryboardHistoryStore((state) =>
    Object.keys(state.visitedUrls[projectId] ?? {}).sort(),
  );

  const urlOptions = useMemo(() => {
    const storyboardUrls = new Set(
      canvas?.storyboards
        ?.map((sb) => sb.route)
        .filter((x): x is string => Boolean(x))
        .filter((url) => !url.startsWith("/tempobook"))
        .sort() ?? [],
    );
    return [
      NewItemSymbol,
      ...Array.from(storyboardUrls).map(
        (url) =>
          ({
            type: "storyboard",
            value: url,
          }) as const,
      ),
      ...Array.from(visitedUrls)
        .filter((url) => !storyboardUrls.has(url))
        .map(
          (url) =>
            ({
              type: "visited",
              value: url,
            }) as const,
        ),
    ];
  }, [canvas?.storyboards, visitedUrls]);

  return (
    <InnerUrlCombobox
      urlOptions={urlOptions}
      disabled={disabled}
      currentUrl={currentUrl}
      devServerUrl={devServerUrl}
      onSubmit={useCallback(
        (newUrl) => {
          useIframePoolStore.getState().setHtmlForStoryboard(storyboardId, "");
          useStoryboardHistoryStore.getState().addVisitedUrl(newUrl, projectId);

          updateCanvasStoryboards(canvasId, [
            {
              changeType: "update",
              storyboardId: storyboardId,
              pageId: storyboard?.pageId || "",
              storyboardUpdateDict: { route: newUrl },
            },
          ]);
        },
        [canvasId, projectId, storyboardId],
      )}
      onRemoveVisitedUrl={useCallback(
        (url) => {
          useStoryboardHistoryStore.getState().removeVisitedUrl(url, projectId);
        },
        [projectId],
      )}
    />
  );
};
